import type { AxiosInstance } from "axios";
import type { UpdateParticipantBody } from "@shared/schemas/event-participant-write.schemas";
import type { GetPhotoUploadUrlResult } from "@shared/schemas/participant-photo.schemas";
import type { ParticipantPreferences } from "@shared/schemas/participant-preferences.schemas";
import {
  EventIdPublic,
  EventParticipantIdPublic,
  UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { GetParticipantsParams } from "@frontend/shared/features/participants/domain/types/participant.types";
import type { DemoPersonaSeedCounts } from "@frontend/shared/features/participants/domain/types/demo-persona-seed.types";

type QueryParamValueType = string | number;

export function createParticipantsApi(apiClient: AxiosInstance) {
  async function getParticipants(
    params: GetParticipantsParams,
  ): Promise<unknown> {
    const queryParams: Record<string, QueryParamValueType> = {};

    if (params.userIdPublic !== undefined) {
      queryParams["userIdPublic"] = UserIdPublic.unwrap(params.userIdPublic);
    }
    if (params.search !== undefined && params.search.trim().length > 0) {
      queryParams["q"] = params.search.trim();
    }
    if (params.status !== undefined) {
      queryParams["status"] = params.status;
    }
    if (params.vip !== undefined) {
      queryParams["vip"] = String(params.vip);
    }
    if (params.hasPendingRequests !== undefined) {
      queryParams["hasPendingRequests"] = String(params.hasPendingRequests);
    }
    if (
      params.externalIdFromImportSource !== undefined &&
      params.externalIdFromImportSource.trim().length > 0
    ) {
      queryParams["externalIdFromImportSource"] =
        params.externalIdFromImportSource.trim();
    }
    if (params.page !== undefined) {
      queryParams["page"] = params.page;
    }
    if (params.limit !== undefined) {
      queryParams["limit"] = params.limit;
    }

    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/participants`,
      {
        params: queryParams,
      },
    );
    return response.data;
  }

  async function getByIdPublic(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}`,
    );
    return response.data;
  }

  async function getMyParticipant(
    eventIdPublic: EventIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/me`,
    );
    return response.data;
  }

  async function updateParticipant(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    data: UpdateParticipantBody,
  ): Promise<unknown> {
    const response = await apiClient.post(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}`,
      data,
    );
    return response.data;
  }

  async function requestPhotoUploadUrl(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    mimeType: string,
  ): Promise<GetPhotoUploadUrlResult> {
    const response = await apiClient.post<GetPhotoUploadUrlResult>(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/photo/upload-url`,
      { mimeType },
    );
    return response.data;
  }

  async function confirmPhotoUpload(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    etag: string,
    photoKey: string,
  ): Promise<unknown> {
    const response = await apiClient.post(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/photo/confirm`,
      { etag, photoKey },
    );
    return response.data;
  }

  async function removePhoto(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<void> {
    await apiClient.post<void>(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/photo/remove`,
    );
  }

  async function putFileToUrl(
    url: string,
    file: File,
  ): Promise<{ readonly etag: string | null }> {
    const response = await apiClient.put<void>(url, file, {
      headers: { "Content-Type": file.type },
    });
    const etag = String(response.headers["etag"] ?? "");
    return { etag: etag.length > 0 ? etag : null };
  }

  async function getPreferences(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/preferences`,
    );
    return response.data;
  }

  async function updatePreferences(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    data: ParticipantPreferences,
  ): Promise<unknown> {
    const response = await apiClient.post(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/preferences`,
      data,
    );
    return response.data;
  }

  async function optInRemember(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<void> {
    await apiClient.put(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/opt-in`,
    );
  }

  async function setParticipantVip(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    vip: boolean,
  ): Promise<void> {
    await apiClient.put<void>(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/vip`,
      { vip },
    );
  }

  async function getActivityLog(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    pagination: { readonly page?: number; readonly limit?: number } = {},
  ): Promise<unknown> {
    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/activity`,
      { params: { role: "host", ...pagination } },
    );
    return response.data;
  }

  async function getMilestones(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<unknown> {
    const response = await apiClient.get(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}/milestones`,
    );
    return response.data;
  }

  async function removeParticipant(
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ): Promise<{ action: string }> {
    const response = await apiClient.delete<{ action: string }>(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}`,
    );
    return response.data;
  }

  async function seedDemoParticipants(
    eventIdPublic: EventIdPublic,
    counts: DemoPersonaSeedCounts,
  ): Promise<unknown> {
    const response = await apiClient.put(
      `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/demo-participants/seed`,
      counts,
    );
    return response.data;
  }

  return {
    getParticipants,
    getByIdPublic,
    getMyParticipant,
    updateParticipant,
    requestPhotoUploadUrl,
    confirmPhotoUpload,
    removePhoto,
    putFileToUrl,
    getPreferences,
    updatePreferences,
    optInRemember,
    setParticipantVip,
    getActivityLog,
    getMilestones,
    removeParticipant,
    seedDemoParticipants,
  } as const;
}
